import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { dateLabel, PERMISSION_LABELS, type Text } from "./language";
import { fmt, late, type CompanyStats } from "./model";
import type { CompanyStatus } from "./use-delivery-companies";

export const STATUS_LABELS = { active: "Active", frozen: "Frozen", stopped: "Stopped" };
export function StatusPill({ status, t }: { status: CompanyStatus; t: Text }) {
  return <span className={`pill ${status === "active" ? "ok" : status === "frozen" ? "chk" : "bad"}`}>{t(STATUS_LABELS[status])}</span>;
}
export function PermissionLine({ permission, t }: { permission: keyof typeof PERMISSION_LABELS; t: Text }) {
  return <small className="sm2 lock">{"\u{1F512} "}{t("Needs the permission: {0}", t(PERMISSION_LABELS[permission]))}</small>;
}
export const balanceLabel = (value: number, t: Text) => t(value > 0 ? "Owes us" : value < 0 ? "Credit in their favour" : "Nothing owed");
export function Balance({ value, t, weight }: { value: number; t: Text; weight?: number }) {
  return <><b className={value < 0 ? "amberT" : ""} style={weight ? { fontWeight: weight } : undefined}>{fmt(value)}</b><span className={`sm2 ${value < 0 ? "amberT" : ""}`}>{balanceLabel(value,t)}</span></>;
}
export function LastCash({ row, t, arabic }: { row: CompanyStats; t: Text; arabic: boolean }) {
  return <>{row.v <= 0 ? <span className="sm2" style={{display:"inline"}}>—</span> :
    <span className={`pill ${late(row) ? "bad" : (row.ds || 0) > 14 ? "chk" : "off"}`}>
      {row.ds === null ? t("Never") : t("{0} days", row.ds)}
    </span>}<span className="sm2">{row.lastRec ? dateLabel(row.lastRec,arabic) : ""}</span></>;
}
export function Modal({ title, children, onClose, busy, t, wide }: {
  title: string; children: ReactNode; onClose: () => void; busy: boolean; t: Text; wide?: boolean;
}) {
  const { i18n } = useTranslation();
  const arabic = (i18n.resolvedLanguage || i18n.language).startsWith("ar");
  const titleId=useId();
  const ref = useRef<HTMLDivElement>(null);
  const [nested,setNested]=useState(false);
  useLayoutEffect(()=>{
    const roots=[...document.querySelectorAll(".delivery-modal-root")];
    setNested(roots.indexOf(ref.current?.closest(".delivery-modal-root") as Element)>0);
  },[]);
  // The prototype overlays the whole application surface, not just its content
  // column. An absolute body portal preserves application-surface positioning
  // in the real Layout without changing the app shell.
  const measureSurface = () => Math.max(document.getElementById("root")?.getBoundingClientRect().height || document.documentElement.scrollHeight, window.innerHeight);
  const [surfaceHeight,setSurfaceHeight] = useState(measureSurface);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  useEffect(()=>{
    const root=document.getElementById("root");
    if(!root)return;
    const observer=new ResizeObserver(()=>setSurfaceHeight(measureSurface()));
    observer.observe(root);
    return ()=>observer.disconnect();
  },[]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      const dialogs=document.querySelectorAll(".delivery-modal-root .md");
      if(dialogs[dialogs.length-1]!==ref.current)return;
      if(event.key === "Escape" && !busyRef.current) closeRef.current();
      if(event.key !== "Tab") return;
      const elements = ref.current?.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex="0"]');
      if (!elements?.length) return;
      const first = elements[0], last = elements[elements.length - 1];
      if(event.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
        event.preventDefault(); last.focus();
      } else if(!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("keydown",handleKey);
    return () => {
      document.removeEventListener("keydown",handleKey);
      previous?.focus();
    };
  },[title]);
  return createPortal(<div className="delivery-companies delivery-modal-root" data-modal-nested={nested} dir={arabic ? "rtl" : "ltr"} lang={arabic ? "ar" : "en"} style={{ minHeight: surfaceHeight }}>
    <div className="ov" onMouseDown={e => { if(e.target === e.currentTarget && !busy) onClose(); }}>
    <div className={`md${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={title?titleId:undefined} aria-label={title?undefined:t("Saved")} aria-busy={busy} tabIndex={-1} ref={ref}>
      <div className="delivery-modal-content">
        <button type="button" className="x" aria-label={t("Close")} onClick={onClose} disabled={busy}>×</button>
        {title&&<h2 id={titleId}>{title}</h2>}{children}
      </div>
    </div>
    </div>
  </div>, document.body);
}
